import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import TwoFactorGate from './components/TwoFactorGate';
import BanScreen from './components/BanScreen';
import { checkAuth, getLoginUrl, logout as apiLogout } from './utils/api';

function useCursorParallax() {
  const rafRef = useRef(null);
  useEffect(() => {
    const root = document.documentElement;
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let curX = targetX;
    let curY = targetY;
    let active = false;

    const tick = () => {
      curX += (targetX - curX) * 0.08;
      curY += (targetY - curY) * 0.08;
      root.style.setProperty('--cursor-x', `${curX}px`);
      root.style.setProperty('--cursor-y', `${curY}px`);
      rafRef.current = requestAnimationFrame(tick);
    };

    const start = () => {
      if (active) return;
      active = true;
      rafRef.current = requestAnimationFrame(tick);
    };
    const stop = () => {
      active = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const onMove = (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      root.classList.add('cursor-engaged');
      start();
    };
    const onLeave = () => {
      root.classList.remove('cursor-engaged');
    };

    const reduced = root.classList.contains('reduce-motion');
    if (!reduced) {
      window.addEventListener('mousemove', onMove, { passive: true });
      window.addEventListener('mouseleave', onLeave);
      start();
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      stop();
      root.classList.remove('cursor-engaged');
    };
  }, []);
}

export default function App() {
  useCursorParallax();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [needsTwoFa, setNeedsTwoFa] = useState(false);
  const [banned, setBanned] = useState(false);
  const [banMessage, setBanMessage] = useState('');

  const refreshSession = useCallback(async () => {
    const data = await checkAuth();
    setAuthenticated(!!data.authenticated);
    setUserEmail(data.email || '');
    setNeedsTwoFa(!!data.authenticated && !!data.needs_two_fa);
    setBanned(!!data.authenticated && !!data.banned);
    setBanMessage(data.ban_message || '');
    return data;
  }, []);

  useEffect(() => {
    refreshSession()
      .catch(() => {
        setAuthenticated(false);
      })
      .finally(() => setLoading(false));
  }, [refreshSession]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const authUrl = await getLoginUrl();
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setLoginError('Could not get login URL. Check your Google credentials.');
      }
    } catch (e) {
      console.error('[App] Login error:', e);
      setLoginError(
        'Failed to connect. Make sure the backend is running and Google OAuth credentials are configured in .env'
      );
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    await apiLogout();
    setAuthenticated(false);
    setUserEmail('');
    setNeedsTwoFa(false);
    setBanned(false);
    setBanMessage('');
  };

  const afterTwoFa = async () => {
    await refreshSession();
  };

  if (loading) {
    return (
      <div className="loading-center app-boot" style={{ minHeight: '100vh' }}>
        <div className="bg-mesh" />
        <div className="loading-spinner" />
        <span>Connecting…</span>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <LoginScreen onLogin={handleLogin} error={loginError} isLoading={loginLoading} />
    );
  }

  if (banned) {
    return <BanScreen message={banMessage} onLogout={handleLogout} />;
  }

  if (needsTwoFa) {
    return <TwoFactorGate userEmail={userEmail} onVerified={afterTwoFa} onLogout={handleLogout} />;
  }

  return <Dashboard userEmail={userEmail} onLogout={handleLogout} />;
}
